import { TouchableOpacity, StyleSheet, View, Platform } from "react-native"
import { router, useFocusEffect, useLocalSearchParams } from "expo-router"
import { useEffect, useRef, useState } from "react"
import { type BottomSheetModal } from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import AnalysisHeaderCompact from "@/components/analysis/AnalysisHeaderCompact"
import CompDetailsHorizontalThumbView from "@/components/analysis/CompDetailsHorizontalThumbView"
import CompCarousel from "@/components/analysis/CompCarousel"
import BackButton from "@/assets/icons/back-arrow-dark.svg"
import { useAnalysisWithComps } from "@/queries/analysis"
import CircularFloatingButton from "@/components/buttons/CircularFloatingButton"
import { $selectedCompDetailId } from "@/stores/compsDataStore"
import { useStore } from "@nanostores/react"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import PropertyInfoModal from "@/components/analysis/modal/PropertyInfoModal"
import { useSafeAreaInsets } from "react-native-safe-area-context"

const CompDetailScreen = () => {
  const { analysisId: paramAnalysisId } = useLocalSearchParams()
  const analysisId = Array.isArray(paramAnalysisId)
    ? paramAnalysisId[0]
    : paramAnalysisId
  const selectedBottomDrawerRef = useRef<BottomSheetModal>(null)
  const { data: detailedAnalysis } = useAnalysisWithComps(analysisId)
  const selectedCompDetailId = useStore($selectedCompDetailId)
  const [carouselDragging, setCarouselDragging] = useState(false)
  const insets = useSafeAreaInsets()

  const analysis = detailedAnalysis?.analysis
  const currentComps = detailedAnalysis?.analysis?.comps ?? null
  const defaultCompId = currentComps?.[0]?.arvcomp?.id!

  useEffect(() => {
    if (!analysis) {
      return
    }

    if (!selectedCompDetailId && defaultCompId) {
      $selectedCompDetailId.set(defaultCompId)
      return
    }

    const selectedComp = analysis?.comps?.find(
      (comp) => comp?.arvcomp?.id === selectedCompDetailId
    )
    const compId = selectedComp?.arvcomp?.id
    const compAddress = selectedComp?.arvcomp?.fullAddress

    if (!compId || !compAddress) {
      return
    }

    AnalyzerEvents.compDetailViewed({
      analysis,
      comp: {
        id: compId,
        address: compAddress,
      },
    })
  }, [analysis, selectedCompDetailId])

  useFocusEffect(() => {
    setStatusBarStyle("dark")
    if (!selectedCompDetailId && analysis) {
      if (currentComps?.length) {
        $selectedCompDetailId.set(defaultCompId)
      }
    }
    selectedBottomDrawerRef.current?.present()
  })

  const handleCarouselDrag = (isDragging: boolean) => {
    setCarouselDragging(isDragging)
  }

  if (!analysis) {
    return null
  }

  return (
    <View style={styles.container}>
      <View className="flex-1 relative">
        <TouchableOpacity
          testID={"back-button"}
          style={[styles.backButton, { paddingTop: insets.top }]}
          onPress={() => {
            $selectedCompDetailId.set(null)
            router.back()
          }}
        >
          <BackButton />
        </TouchableOpacity>

        <View
          className="flex flex-row w-full shrink-0"
          style={{ paddingTop: 10 }}
        >
          <AnalysisHeaderCompact analysis={analysis} />
        </View>
        <CompDetailsHorizontalThumbView
          analysis={analysis}
          carouselDragging={carouselDragging}
        />
        {selectedCompDetailId && (
          <CompCarousel
            analysis={analysis}
            selectedCompDetailId={selectedCompDetailId}
            onDragStateChange={handleCarouselDrag}
          />
        )}

        <View
          style={{
            bottom: 20,
            right: 20,
            position: "absolute",
          }}
        >
          <CircularFloatingButton
            analysisId={analysis?.id}
            type="viewMap"
            onPress={() => {
              router.push(`/properties/${analysis?.id}/map`)
            }}
            style={{ marginBottom: 17 }}
          />

          <CircularFloatingButton
            analysisId={analysis?.id}
            type="getLoan"
            onPress={() => {
              router.push(`/properties/${analysis?.id}/map`)
            }}
          />
        </View>
      </View>

      <PropertyInfoModal analysis={analysis}></PropertyInfoModal>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "flex-start",
    width: "100%",
    height: "100%",
    backgroundColor: BFColors.offWhite,
  },
  backButton: {
    position: "absolute",
    top: Platform.OS === "android" ? 24 : 12, // Conditional padding for proper Android positioning
    left: 0,
    zIndex: 10,
    padding: 16,
  },
})

export default CompDetailScreen
